import React, { useState, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Backdrop,
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import Swal from 'sweetalert2';
import MDDatePicker from 'components/MDDatePicker';
import printPatientCard from 'utils/printPatientCard';

const TEAL = '#00798E';
const selectBoxHeight = 45;

const RELIGION_OPTIONS = ['Hinduism', 'Islam', 'Christianity', 'Sikhism', 'Buddhism', 'Jainism', 'Others'];

const defaultValues = {
  first_name: '',
  last_name: '',
  gender: 'F',
  marital_status: 'married',
  age: '',
  married_for: '',
  trying_for: '',
  nationality: 'Indian',
  religion: '',
  mobile: '',
  occupation: '',
  socio_economic_status: '',
  address_line1: '',
  address_line2: '',
  state: '',
  country_id: 100,
  city: '',
  pincode: '',
  spouse_first_name: '',
  spouse_last_name: '',
  spouse_gender: 'M',
  spouse_age: '',
  spouse_mobile: '',
  doctor_id: '',
  enrolled_date: '',
};

function sectionHeader(title) {
  return (
    <Box sx={{ mb: 2, mt: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{ color: TEAL, fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}
      >
        {title}
      </Typography>
      <Divider sx={{ borderColor: TEAL, mt: 0.5, opacity: 0.4 }} />
    </Box>
  );
}

export default function OldPatientRegistration() {
  const [loading, setLoading] = useState(false);
  const [mobileError, setMobileError] = useState('');
  const [spouseMobileError, setSpouseMobileError] = useState('');
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [socioEconomicOptions, setSocioEconomicOptions] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues });

  const mobileValue = watch('mobile');
  const spouseMobileValue = watch('spouse_mobile');
  const countryIdValue = watch('country_id');

  useEffect(() => {
    api.master.getActiveCountries().then((res) => {
      if (res?.data?.countries?.length) setCountries(res.data.countries);
    });
    api.master.getActiveStates(100).then((res) => {
      if (res?.data?.states?.length) setStates(res.data.states);
    });
    api.contacts.getDoctorsList().then((res) => {
      if (res?.data?.length) setDoctors(res.data);
    });
    api.contacts.getPatientParams().then((res) => {
      const members = res?.data?.params?.param_group_members;
      const socioEconomicStatusMember = members?.find((m) => m.name === 'Socio Economic Status');
      if (socioEconomicStatusMember) {
        setSocioEconomicOptions(socioEconomicStatusMember.param_group_member_options || []);
      }
    });
  }, []);

  const handleCountryChange = () => {
    setValue('state', '');
  };

  const handleNationalityChange = (nationalityValue) => {
    const match = countries.find(c => c.nationality === nationalityValue);
    if (match) {
      setValue('country_id', match.id);
      setValue('state', '');
    }
  };

  const checkMobile = useCallback(async (mobile, field) => {
    if (!mobile || mobile.length < 10) return;
    try {
      const res = await api.contacts.checkOldPatientMobile(mobile);
      if (res?.data?.exists) {
        if (field === 'mobile') setMobileError('This mobile number is already registered.');
        else setSpouseMobileError('This mobile number is already registered.');
      } else {
        if (field === 'mobile') setMobileError('');
        else setSpouseMobileError('');
      }
    } catch {
      // silently ignore
    }
  }, []);

  const onSubmit = async (values) => {
    if (mobileError || spouseMobileError) {
      Swal.fire({ icon: 'warning', title: 'Duplicate Mobile', text: 'Please fix the mobile number errors before submitting.' });
      return;
    }
    if (!values.first_name?.trim()) {
      Swal.fire({ icon: 'warning', title: 'Validation', text: 'First name is required.' });
      return;
    }
    if (!values.mobile || values.mobile.length < 10) {
      Swal.fire({ icon: 'warning', title: 'Validation', text: 'A valid 10-digit mobile number is required.' });
      return;
    }
    if (!values.doctor_id) {
      Swal.fire({ icon: 'warning', title: 'Validation', text: 'Please select a doctor to consult with.' });
      return;
    }

    const payload = { ...values };
    if (!payload.spouse_first_name?.trim()) {
      delete payload.spouse_first_name;
      delete payload.spouse_last_name;
      delete payload.spouse_gender;
      delete payload.spouse_age;
      delete payload.spouse_mobile;
    }
    ['age', 'spouse_age'].forEach(f => { if (payload[f]) payload[f] = parseInt(payload[f], 10); });
    Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });

    setLoading(true);
    try {
      const res = await api.contacts.registerOldPatient(payload);
      const apiData = res?.data;
      setLoading(false);

      // Merge API result (IDs) with form values (name, age, mobile, etc.) for card print
      const record = { ...payload, ...apiData };

      const result = await Swal.fire({
        icon: 'success',
        title: 'Patient Registered!',
        html: `<b>Patient ID:</b> ${apiData?.patientExternalCode || ''}<br/><b>Couple ID:</b> ${apiData?.coupleExternalCode || ''}`,
        showCancelButton: true,
        confirmButtonText: 'Print Card',
        cancelButtonText: 'Close',
        confirmButtonColor: TEAL,
      });

      if (result.isConfirmed && record) {
        printPatientCard(record);
      }
      reset(defaultValues);
    } catch (err) {
      setLoading(false);
      const msg = err?.response?.data?.message || 'Registration failed. Please try again.';
      Swal.fire({ icon: 'error', title: 'Error', text: msg });
    }
  };

  const inputSx = {
    '& .MuiInputBase-root': { height: selectBoxHeight },
    '& .MuiInputBase-input': { textTransform: 'uppercase' },
  };

  const upperCaseOnChange = (field) => (e) => field.onChange(e.target.value.toUpperCase());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card sx={{ p: { xs: 2, md: 3 } }}>
          {/* Page Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <PersonAddIcon sx={{ color: TEAL, fontSize: 28 }} />
            <MDTypography variant="h5" fontWeight="bold" sx={{ color: TEAL }}>
              Old Patient Registration
            </MDTypography>
            <Typography variant="body2" sx={{ color: '#888', ml: 1 }}>
              (Data Migration — ID format: IRMP-O-…)
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>

              {/* ── Patient Information ── */}
              <Grid item xs={12}>{sectionHeader('Patient Information')}</Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="first_name" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="First Name *" fullWidth size="small" sx={inputSx} error={!!errors.first_name} helperText={errors.first_name?.message} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="last_name" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="Last Name" fullWidth size="small" sx={inputSx} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small" sx={inputSx}>
                  <InputLabel>Gender</InputLabel>
                  <Controller name="gender" control={control} render={({ field }) => (
                    <Select {...field} label="Gender">
                      <MenuItem value="F">Female</MenuItem>
                      <MenuItem value="M">Male</MenuItem>
                      <MenuItem value="O">Other</MenuItem>
                    </Select>
                  )} />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small" sx={inputSx}>
                  <InputLabel>Marital Status</InputLabel>
                  <Controller name="marital_status" control={control} render={({ field }) => (
                    <Select {...field} label="Marital Status">
                      <MenuItem value="married">Married</MenuItem>
                      <MenuItem value="single">Single</MenuItem>
                      <MenuItem value="separated">Separated</MenuItem>
                      <MenuItem value="widowed">Widowed</MenuItem>
                      <MenuItem value="divorced">Divorced</MenuItem>
                      <MenuItem value="unspecified">Unspecified</MenuItem>
                    </Select>
                  )} />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="age" control={control} render={({ field }) => (
                  <TextField {...field} label="Age (years)" type="number" fullWidth size="small" sx={inputSx} inputProps={{ min: 0, max: 120 }} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="mobile" control={control} render={({ field }) => (
                  <TextField
                    {...field}
                    label="Mobile Number *"
                    fullWidth size="small" sx={inputSx}
                    error={!!mobileError}
                    helperText={mobileError}
                    inputProps={{ maxLength: 15 }}
                    onBlur={() => checkMobile(field.value, 'mobile')}
                  />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="married_for" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="Married For (optional)" fullWidth size="small" sx={inputSx} placeholder="e.g. 2 years" />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="trying_for" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="Trying For (optional)" fullWidth size="small" sx={inputSx} placeholder="e.g. 1.5 years" />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="nationality" control={control} render={({ field }) => (
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Nationality</InputLabel>
                    <Select
                      {...field}
                      label="Nationality"
                      onChange={(e) => {
                        field.onChange(e);
                        handleNationalityChange(e.target.value);
                      }}
                    >
                      <MenuItem value="Indian">Indian</MenuItem>
                      <MenuItem value="Bangladeshi">Bangladeshi</MenuItem>
                      <MenuItem value="Nepali">Nepali</MenuItem>
                      <MenuItem value="Sri Lankan">Sri Lankan</MenuItem>
                      <MenuItem value="Pakistani">Pakistani</MenuItem>
                      <MenuItem value="American">American</MenuItem>
                      <MenuItem value="British">British</MenuItem>
                      <MenuItem value="Canadian">Canadian</MenuItem>
                      <MenuItem value="Australian">Australian</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="religion" control={control} render={({ field }) => (
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Religion</InputLabel>
                    <Select {...field} label="Religion">
                      {RELIGION_OPTIONS.map((r) => (
                        <MenuItem key={r} value={r}>{r}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />
              </Grid>



              <Grid item xs={12} sm={6} md={4}>
                <Controller name="occupation" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="Occupation (optional)" fullWidth size="small" sx={inputSx} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="socio_economic_status" control={control} render={({ field }) => (
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Socio-Economic Status (optional)</InputLabel>
                    <Select {...field} label="Socio-Economic Status (optional)">
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {socioEconomicOptions.map((option) => (
                        <MenuItem key={option.id} value={option.option_value}>
                          {option.option_value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />
              </Grid>

              {/* ── Address ── */}
              <Grid item xs={12}>{sectionHeader('Address')}</Grid>

              <Grid item xs={12} sm={6}>
                <Controller name="address_line1" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="Address Line 1" fullWidth size="small" sx={inputSx} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller name="address_line2" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="Address Line 2" fullWidth size="small" sx={inputSx} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Controller name="city" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="City" fullWidth size="small" sx={inputSx} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Controller name="state" control={control} render={({ field }) => (
                  countryIdValue === 100 ? (
                    <FormControl fullWidth size="small" sx={inputSx}>
                      <InputLabel>State</InputLabel>
                      <Select
                        {...field}
                        label="State"
                        value={states.find(s => s.name === field.value)?.id || ''}
                        onChange={(e) => {
                          const sel = states.find(s => s.id === e.target.value);
                          if (sel) field.onChange(sel.name);
                        }}
                      >
                        {states.map(s => (
                          <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField {...field} onChange={upperCaseOnChange(field)} label="State" fullWidth size="small" sx={inputSx} />
                  )
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Controller name="country_id" control={control} render={({ field }) => (
                  <FormControl fullWidth size="small" sx={inputSx}>
                    <InputLabel>Country</InputLabel>
                    <Select
                      {...field}
                      label="Country"
                      onChange={(e) => {
                        field.onChange(e);
                        handleCountryChange();
                      }}
                    >
                      {countries.map(c => (
                        <MenuItem key={c.id} value={c.id}>{c.country_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Controller name="pincode" control={control} render={({ field }) => (
                  <TextField {...field} label="Pin / Zip Code" fullWidth size="small" sx={inputSx} inputProps={{ maxLength: 10 }} />
                )} />
              </Grid>

              {/* ── Spouse Information ── */}
              <Grid item xs={12}>{sectionHeader('Spouse Information (optional)')}</Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="spouse_first_name" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="Spouse First Name" fullWidth size="small" sx={inputSx} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="spouse_last_name" control={control} render={({ field }) => (
                  <TextField {...field} onChange={upperCaseOnChange(field)} label="Spouse Last Name" fullWidth size="small" sx={inputSx} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small" sx={inputSx}>
                  <InputLabel>Spouse Gender</InputLabel>
                  <Controller name="spouse_gender" control={control} render={({ field }) => (
                    <Select {...field} label="Spouse Gender">
                      <MenuItem value="M">Male</MenuItem>
                      <MenuItem value="F">Female</MenuItem>
                      <MenuItem value="O">Other</MenuItem>
                    </Select>
                  )} />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="spouse_age" control={control} render={({ field }) => (
                  <TextField {...field} label="Spouse Age (years)" type="number" fullWidth size="small" sx={inputSx} inputProps={{ min: 0, max: 120 }} />
                )} />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Controller name="spouse_mobile" control={control} render={({ field }) => (
                  <TextField
                    {...field}
                    label="Spouse Mobile"
                    fullWidth size="small" sx={inputSx}
                    error={!!spouseMobileError}
                    helperText={spouseMobileError}
                    inputProps={{ maxLength: 15 }}
                    onBlur={() => checkMobile(field.value, 'spouse_mobile')}
                  />
                )} />
              </Grid>

              {/* ── Consultation Info ── */}
              <Grid item xs={12}>{sectionHeader('Consultation Details')}</Grid>

              <Grid item xs={12} sm={6}>
                <Controller name="doctor_id" control={control} render={({ field }) => (
                  <FormControl fullWidth size="small" sx={inputSx} error={!!errors.doctor_id}>
                    <InputLabel>Consult With (Doctor) *</InputLabel>
                    <Select {...field} label="Consult With (Doctor) *">
                      {doctors.map((doctor) => (
                        <MenuItem key={doctor.user_id} value={doctor.user_id}>
                          {doctor.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller name="enrolled_date" control={control} render={({ field }) => (
                  <MDDatePicker
                    value={field.value || ''}
                    onChange={(date) => field.onChange(date || '')}
                    label="Enrolled Date"
                  />
                )} />
              </Grid>

              {/* ── Submit ── */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{
                      bgcolor: TEAL,
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 4,
                      '&:hover': { bgcolor: '#005f72' },
                    }}
                  >
                    Register Patient
                  </Button>
                </Box>
              </Grid>

            </Grid>
          </form>
        </Card>
      </MDBox>
      <Footer />

      <Backdrop open={loading} sx={{ zIndex: 9999 }}>
        <CircularProgress sx={{ color: '#fff' }} />
      </Backdrop>
    </DashboardLayout>
  );
}
